import { type ParsedResult, type ParsingOption } from 'chrono-node';
import * as chronoDe from 'chrono-node/de';
import * as chronoEn from 'chrono-node/en';
import * as chronoEs from 'chrono-node/es';
import * as chronoFr from 'chrono-node/fr';
import * as chronoIt from 'chrono-node/it';
import { endOfMonth, endOfWeek, endOfYear, startOfMonth, startOfWeek, startOfYear } from 'date-fns';

import { fromIsoDay } from './format';
import { type DateLocale } from './localization';

/** Free-text dates via `chrono-node`, plus English `start/end of week/month/year`; local midnight, or `null`. */
export function parseSmartDate(input: string, locale: DateLocale, now: Date = new Date()): Date | null {
    const trimmed = input.trim();
    if (!trimmed) return null;

    const boundary = parseBoundaryKeyword(trimmed, now);
    if (boundary) return boundary;

    const chrono = CHRONO_BY_LOCALE[locale];
    const results = chrono.parse(trimmed, now, { forwardDate: false });
    if (results.length > 0) {
        const parsed = results[0].start.date();
        if (!isNaN(parsed.getTime())) return atMidnight(parsed);
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        const d = fromIsoDay(trimmed);
        if (!isNaN(d.getTime())) return d;
    }

    return null;
}

// The locale modules' `parse` signatures differ; we only pass a `Date`, which all of them take.
type ChronoLocale = {
    parse: (text: string, ref?: Date, option?: ParsingOption) => ParsedResult[];
};

const CHRONO_BY_LOCALE: Record<DateLocale, ChronoLocale> = {
    en: chronoEn,
    de: chronoDe,
    fr: chronoFr,
    es: chronoEs,
    it: chronoIt,
};

function atMidnight(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** `chrono-node` lacks these; English only, as other locales rarely use them. */
function parseBoundaryKeyword(input: string, now: Date): Date | null {
    const text = input.toLowerCase();
    const today = atMidnight(now);
    const weekStartsOn = 1 as const;

    switch (text) {
        case 'start of month':
            return startOfMonth(today);
        case 'end of month':
            return endOfMonth(today);
        case 'start of week':
            return startOfWeek(today, { weekStartsOn });
        case 'end of week':
            return endOfWeek(today, { weekStartsOn });
        case 'start of year':
            return startOfYear(today);
        case 'end of year':
            return endOfYear(today);
        default:
            return null;
    }
}
